import { useState, useEffect, useCallback } from 'react';
import { useTranslate, useDataProvider, useNotify } from 'react-admin';
import {
  Box,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

interface TagOption {
  id: number;
  name: string;
  color?: string | null;
}

interface TicketTagSelectorProps {
  ticketId: number;
  tags: TagOption[];
  onTagsChange?: (tags: TagOption[]) => void;
}

export function TicketTagSelector({
  ticketId,
  tags,
  onTagsChange,
}: TicketTagSelectorProps) {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);

  // Fetch available ticket-domain tags
  useEffect(() => {
    if (!showAutocomplete) return;
    setLoading(true);
    dataProvider
      .getList('tag', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: { domain_eq: 'ticket' },
        meta: { useReferenceEndpoint: true },
      })
      .then(({ data }: { data: TagOption[] }) => {
        setAvailableTags(data);
      })
      .catch(() => {
        notify('ra.notification.http_error', { type: 'error' });
      })
      .finally(() => setLoading(false));
  }, [showAutocomplete, dataProvider, notify]);

  const syncTags = useCallback(
    async (newTagIds: number[], rollback: TagOption[]) => {
      setSyncing(true);
      try {
        await (dataProvider as any).httpRequest(`ticket/${ticketId}/tags`, {
          method: 'POST',
          body: JSON.stringify({ tagIds: newTagIds }),
        });
      } catch {
        notify('ra.notification.http_error', { type: 'error' });
        onTagsChange?.(rollback);
      } finally {
        setSyncing(false);
      }
    },
    [dataProvider, ticketId, notify, onTagsChange],
  );

  const handleRemoveTag = useCallback(
    async (tagId: number) => {
      const previousTags = tags;
      const newTags = tags.filter((t) => t.id !== tagId);
      onTagsChange?.(newTags);
      await syncTags(newTags.map((t) => t.id), previousTags);
    },
    [tags, onTagsChange, syncTags],
  );

  const handleAddTag = useCallback(
    async (_event: unknown, value: TagOption | null) => {
      if (!value) return;
      if (tags.some((t) => t.id === value.id)) return;
      const previousTags = tags;
      const newTags = [...tags, value];
      onTagsChange?.(newTags);
      setShowAutocomplete(false);
      await syncTags(newTags.map((t) => t.id), previousTags);
    },
    [tags, onTagsChange, syncTags],
  );

  const unselectedTags = availableTags.filter(
    (at) => !tags.some((t) => t.id === at.id),
  );

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
        {tags.map((tag) => (
          <Chip
            key={tag.id}
            label={tag.name}
            size="small"
            onDelete={() => handleRemoveTag(tag.id)}
            disabled={syncing}
            sx={{
              ...(tag.color
                ? {
                    backgroundColor: tag.color,
                    color: '#fff',
                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' },
                  }
                : {}),
            }}
          />
        ))}
        {!showAutocomplete && (
          <Chip
            icon={<AddIcon />}
            label={translate('resources.tag.label')}
            size="small"
            variant="outlined"
            onClick={() => setShowAutocomplete(true)}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </Box>
      {showAutocomplete && (
        <Autocomplete
          size="small"
          options={unselectedTags}
          getOptionLabel={(option) => option.name}
          loading={loading}
          onChange={handleAddTag}
          onClose={() => setShowAutocomplete(false)}
          openOnFocus
          autoFocus
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box display="flex" alignItems="center" gap={1}>
                {option.color && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '2px',
                      backgroundColor: option.color,
                    }}
                  />
                )}
                {option.name}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={translate('ra.action.search')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mt: 0.5 }}
        />
      )}
    </Box>
  );
}
